
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../styles/ui.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api';
const roleNames = { 2: 'ERC Staff', 3: 'Reviewer', 4: 'Secretary' };

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const roleId = parseInt(localStorage.getItem('role_id'), 10);
  const navigate = useNavigate();

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/users`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleUserStatus = async (id, isActive) => {
    try {
      await fetch(`${API_BASE_URL}/admin/users/status/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ is_active: !isActive }),
      });
      fetchUsers();
    } catch (err) {
      console.error('Failed to update user status:', err);
    }
  };


  const handleResetPassword = async (id) => {
    const newPassword = window.prompt('Enter a new password for this user:');
    if (!newPassword) return;
    try {
      const res = await fetch(`${API_BASE_URL}/admin/users/reset-password/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ newPassword }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Password reset failed');
        return;
      }
      alert('Password reset successfully');
    } catch (err) {
      console.error('Password reset failed:', err);
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/admin/users/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      if (!res.ok) alert(data.error || 'Delete failed');
      else {
        alert('User deleted successfully');
        fetchUsers();
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('An error occurred while deleting');
    }
  };

  const handleForceDeleteUser = async (id) => {
    if (!window.confirm('This will delete the user and all related data. Proceed?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/admin/users/${id}/force`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      if (!res.ok) alert(data.error || 'Force delete failed');
      else {
        alert('User and dependencies deleted');
        fetchUsers();
      }
    } catch (err) {
      console.error('Force delete error:', err);
      alert('An error occurred while force deleting');
    }
  };

  const filteredUsers = users.filter((user) => {
    const query = (searchTerm || '').toLowerCase();
    return (
      (user.first_name || '').toLowerCase().includes(query) ||
      (user.last_name || '').toLowerCase().includes(query) ||
      (user.email || '').toLowerCase().includes(query) ||
      (roleNames[user.role_id] || '').toLowerCase().includes(query) ||
      ((user.is_active ? 'active' : 'inactive')).includes(query)
    );
  });

  const verifiedPill = (isVerified) =>
    isVerified ? (
      <span className="child-status approved">Verified</span>
    ) : (
      <span className="child-status rejected">Not Verified</span>
    );

  const activePill = (isActive) =>
    isActive ? (
      <span className="document-status accepted">Active</span>
    ) : (
      <span className="document-status none">Inactive</span>
    );

  if (loading) {
    return (
      <div className="proposal-details-container">
        <div className="card"><div className="empty-state">Loading…</div></div>
      </div>
    );
  }
  if (roleId !== 2 && roleId !== 4) {
    return (
      <div className="proposal-details-container">
        <div className="card"><div className="error-text">Access Denied</div></div>
      </div>
    );
  }

  return (
    <div className="proposal-details-container">
      <div className="card">
        <div className="details-header">
          <h2>User Management</h2>
          <span className="status-pill-header">{filteredUsers.length} user(s)</span>
        </div>

        {}
        <div className="card" style={{ marginTop: 8 }}>
          <div className="details-grid" style={{ alignItems: 'end' }}>
            <div>
              <button className="btn sm primary" onClick={() => navigate('/admin/create-user')}>
                + Create User
              </button>
            </div>
            <div>
              <div className="muted">Search</div>
              <input
                className="input-std"
                type="text"
                placeholder="Search by name, email, role…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {}
        <div className="card" style={{ marginTop: 12 }}>
          <table className="table-std">
            <thead>
              <tr>
                <th>#</th>
                <th>Full Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Verified</th>
                <th>Active</th>
                <th>Toggle</th>
                <th style={{ width: 300 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="empty-state">No users match your search.</td>
                </tr>
              ) : (
                filteredUsers.map((user, index) => (
                  <tr
                    key={user.id}
                    onClick={() => navigate(`/admin/users/${user.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{index + 1}</td>
                    <td>{user.first_name} {user.last_name}</td>
                    <td>{user.email}</td>
                    <td>{roleNames[user.role_id] || '-'}</td>
                    <td>{verifiedPill(user.is_verified)}</td>
                    <td>{activePill(user.is_active)}</td>
                    <td>
                      <button
                        className={user.is_active ? 'btn sm danger-outline' : 'btn sm approve'}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleUserStatus(user.id, user.is_active);
                        }}
                        title={user.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {user.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                    <td>
                      <div
                        className="row-actions"
                        style={{ display: 'flex', gap: 8 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {}
                        <button className="btn sm primary" onClick={() => handleResetPassword(user.id)}>
                          Reset
                        </button>
                        <button className="btn sm danger-outline" onClick={() => handleDeleteUser(user.id)}>
                          Delete
                        </button>
                        <button className="btn sm reject" onClick={() => handleForceDeleteUser(user.id)}>
                          Force Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
